import * as React from 'react'
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import { Constraint } from '../../types/Work/Constraint'
import { StyledTableCell } from '../core/StyledTableCell'
import { formatLocaleShortDateTime } from '../../services/CommonServices/DataFormatters'
import { AttachFile, Clear } from '@mui/icons-material'
import { useState } from 'react'
import { getConstraintAttachment } from '../../services/workItemService'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import { ImageSlider } from '../core/ImageSlider'

type ConstraintsListProps = {
	constraints: Constraint[] | undefined
	onConfirmClearConstraint: (constraint: Constraint) => void
}

export function ConstraintsList({ constraints, onConfirmClearConstraint }: ConstraintsListProps) {

	const [open, setOpen] = useState<boolean>(false)
	const [imageBlobs, setImageBlobs] = useState<Blob[]>([])
	const [loadingImages, setLoadingImages] = useState(false)
	const { projectTimeZone } = useProjectFeaturesContext()

	const showDialog = async (constraint: Constraint) => {
		setOpen(true)
		setImageBlobs([])
		setLoadingImages(true)
		try {
			const response = await getConstraintAttachment(constraint)
			const urls = response.map((x) => `${x.uri}${x.sasToken}`)
			const blobs = await Promise.all(
				urls.map((url) => fetch(url).then((r) => r.blob())),
			)
			setImageBlobs(blobs)
		} finally {
			setLoadingImages(false)
		}
	}

	function handleDialogClose(): void {
		setOpen(false)
		setImageBlobs([])
	}


	return (
		<>
			<Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
				<DialogTitle>Constraint Attachment</DialogTitle>
				<DialogContent>
					<Box sx={{ display: 'flex', width: '100%', minHeight: 200, justifyContent: 'center', alignItems: 'center' }}>
						{loadingImages ? (
							<CircularProgress />
						) : imageBlobs.length > 0 ? (
							<ImageSlider blobs={imageBlobs} readOnly={true} />
						) : (
							!loadingImages && <span>No images</span>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDialogClose} variant="contained" color="primary">
						Close
					</Button>
				</DialogActions>
			</Dialog>
			<TableContainer
				sx={{
					height: '100%',
					width: '100%',
					overflowX: 'scroll',
					overflowY: 'scroll',
				}}
			>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<StyledTableCell align='left'>Id</StyledTableCell>
							<StyledTableCell align='left'>Work Item Name</StyledTableCell>
							<StyledTableCell align='left'>Comment</StyledTableCell>
							<StyledTableCell align='center'>Reported By</StyledTableCell>
							<StyledTableCell align='center'>Reported At</StyledTableCell>
							<StyledTableCell align='center'>Cleared At</StyledTableCell>
							<StyledTableCell align='center'>Image</StyledTableCell>
							<StyledTableCell align='center'>Actions</StyledTableCell>
						</TableRow>
					</TableHead>

					<TableBody>
						{(constraints ?? []).map((constraint: Constraint) => (
							<TableRow key={constraint.id}>
								<TableCell align='left'>{constraint.id}</TableCell>
								<TableCell align='left'>{constraint.workItemName}</TableCell>
								<TableCell align='left'>{constraint.comments}</TableCell>
								<TableCell align='center'>{constraint.reportedByName}</TableCell>
								<TableCell align='center'>
									{formatLocaleShortDateTime(constraint.timeReported, projectTimeZone ?? 'UTC')}
								</TableCell>
								<TableCell align='center'>
									{formatLocaleShortDateTime(constraint.timeCleared, projectTimeZone ?? 'UTC')}
								</TableCell>
								<TableCell align='center'>
									{constraint.attachments.length > 0 && (
										<IconButton onClick={() => showDialog(constraint)} color='info'>
											<AttachFile
												sx={{
													color: '#FFFFFF',
													background: '#0DAEFF',
													padding: '2px',
													borderRadius: '4px',
												}}
											/>
										</IconButton>
									)}
								</TableCell>
								<TableCell align='center'>
									{!constraint.timeCleared && (
										<IconButton color='error' onClick={() => onConfirmClearConstraint(constraint)}>
											<Clear />
										</IconButton>
									)}
								</TableCell>
							</TableRow>
						))}
						{(constraints?.length ?? 0) === 0 && (
							<TableRow>
								<TableCell align='center' scope='row' colSpan={17}>
									<h4 style={{ fontStyle: 'italic' }}>No Constraints were found</h4>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	)
}
